import type { ProductVariantCustomFields, ProductVariantPriceTiersStruct } from '~/generated/graphql';

export function calculateActivePriceTier(
    priceTiers: ProductVariantCustomFields['priceTiers'] | undefined | null,
    quantity: number,
): ProductVariantPriceTiersStruct | null {
    if (!priceTiers || !Array.isArray(priceTiers) || priceTiers.length === 0 || quantity <= 0) {
        return null;
    }

    // Find a tier that matches the quantity range
    const matchingTier = priceTiers
        .filter((tier) => {
            const meetsMin = typeof tier?.minQty === 'number' && quantity >= tier.minQty;
            const withinMax = tier?.maxQty == null || quantity <= tier.maxQty;
            return meetsMin && withinMax;
        })
        .sort((a, b) => (b.minQty ?? 0) - (a.minQty ?? 0))[0];

    if (matchingTier) {
        return matchingTier;
    }

    const highestTier = priceTiers
        .filter((tier) => typeof tier?.minQty === 'number' && quantity >= tier.minQty)
        .sort((a, b) => (b.minQty ?? 0) - (a.minQty ?? 0))[0];

    return highestTier ?? null;
}

export function calculateEffectivePrice(
    basePrice: number,
    priceTiers: ProductVariantCustomFields['priceTiers'] | undefined | null,
    quantity: number,
): {
    effectiveUnitPrice: number;
    activeTier: ProductVariantPriceTiersStruct | null;
} {
    const activeTier = calculateActivePriceTier(priceTiers, quantity);

    const effectiveUnitPrice =
        activeTier && typeof activeTier.price === 'number'
            ? activeTier.price * 100 // Tier prices are in Rs, multiply by 100 to convert to paisa
            : basePrice;

    return {
        effectiveUnitPrice,
        activeTier,
    };
}

export function calculateLineTotal(
    basePrice: number,
    priceTiers: ProductVariantCustomFields['priceTiers'] | undefined | null,
    quantity: number,
): {
    effectiveUnitPrice: number;
    totalPrice: number;
    activeTier: ProductVariantPriceTiersStruct | null;
} {
    const { effectiveUnitPrice, activeTier } = calculateEffectivePrice(basePrice, priceTiers, quantity);

    return {
        effectiveUnitPrice,
        totalPrice: effectiveUnitPrice * quantity,
        activeTier,
    };
}
